import {useEffect,useMemo,useRef,useState} from 'react';
import {useIntl} from '@/react-intl';
import {Button} from '@/design-system/ds-0/components/Button';
import StudioCheckbox from '../shared/StudioCheckbox';
import {useModuleStudio} from '../data/ModuleStudioDataProvider';
import type {DesignModuleSpecJson} from '../data/contracts';
import {loadBindingSet} from './measuredNativeBindings';
import {applyLayout,proposeLayouts,type LayoutProposal} from './layoutDetection';

/**
 * CMDB-LAYOUT (GAP-DISPOSITION) : dans « Relier », chaque disposition reconnue dans le prototype (façade de baie, plan à couches)
 * est proposée décochée, avec son modèle (conteneur, positions « contenu dans », capacité, zones, catégories) et ce qui reste
 * non relié ; un geste du Maker l'applique au brouillon. La simulation chargée (Relier mesuré) apparie seulement les styles et
 * le titre ; sans elle, rien n'est deviné.
 */
const T='moduleStudio.template.layout.';
/** Signes de mise en page (aucun texte de l'utilisateur). */
const SEPARATOR=' · ',DASH_SEPARATOR=' — ';

type Samples=Record<string,Record<string,unknown>[]>;
export default function LayoutDetectedReview({dossierId,spec,apply}:{dossierId:string;spec:DesignModuleSpecJson;apply:(next:DesignModuleSpecJson,page:string)=>void}){
 const intl=useIntl(),t=(key:string,values?:Record<string,string|number>)=>intl.formatMessage({id:T+key},values);
 const {repository}=useModuleStudio(),source=useRef(repository);source.current=repository;
 const moduleCode=String(spec.module.code??'');
 const [samples,setSamples]=useState<Samples|undefined>(),[ready,setReady]=useState(false),[checked,setChecked]=useState<Record<string,boolean>>({});
 useEffect(()=>{let active=true;void loadBindingSet(source.current,dossierId,moduleCode).then(value=>{if(active){setSamples(value.simulation?value.samples as Samples:undefined);setReady(true);}},()=>{if(active){setSamples(undefined);setReady(true);}});return()=>{active=false;};},[dossierId,moduleCode]);
 const proposals=useMemo(()=>ready?proposeLayouts(spec,samples):[],[spec,samples,ready]);
 if(!proposals.length)return null;
 const id=(proposal:LayoutProposal)=>proposal.page+'#'+proposal.block+'#'+proposal.kind;
 const label=(key:string)=>{const page=(spec.pages as {key:string;label?:Record<string,string>}[]).find(item=>item.key===key);return page?.label?.[String(intl.locale)]??page?.label?.fr??key;};
 const entity=(code?:string)=>{if(!code)return '—';const value=(spec.entities as Record<string,{label?:{singular?:Record<string,string>}}>)[code]?.label?.singular;return value?.[String(intl.locale)]??value?.fr??code;};
 return <fieldset><legend>{t('title')}</legend>
  <p>{t('help')}</p>
  {proposals.map(proposal=>{const s=proposal.node.source;return <div key={id(proposal)}>
   <p><strong>{label(proposal.page)}</strong>{SEPARATOR}{t('kind.'+proposal.kind)}</p>
   <ul>
    <li>{t('container',{entity:entity(s.container.entity),field:s.container.display})}</li>
    <li>{t('positions',{entity:entity(s.positions.entity),fields:[s.positions.start,s.positions.size,s.positions.x,s.positions.y].filter(Boolean).join(', ')})}</li>
    {s.capacity?<li>{t('capacity',{entity:entity(s.capacity.entity),field:s.capacity.field})}</li>:null}
    {s.zones?<li>{t('zones',{entity:entity(s.zones.entity)})}</li>:null}
    {s.items.category?<li>{t('category',{field:s.items.category.field})}</li>:null}
    {s.layers?<li>{t('layers',{field:s.layers.field})}</li>:null}
    <li>{t('realized',{count:proposal.realized})}</li>
   </ul>
   {proposal.residual.length?<details><summary>{t('residual',{count:proposal.residual.length})}</summary><ul>{proposal.residual.map((item,index)=><li key={index}><bdi>{item.part}</bdi>{DASH_SEPARATOR}{t('cause.'+item.cause)}</li>)}</ul></details>:null}
   <StudioCheckbox checked={!!checked[id(proposal)]} onChange={event=>setChecked(previous=>({...previous,[id(proposal)]:event.target.checked}))}>{t('accept')}</StudioCheckbox>
  </div>;})}
  <Button disabled={!proposals.some(proposal=>checked[id(proposal)])} onClick={()=>{
   let next=spec;const pages:string[]=[];
   for(const proposal of proposals)if(checked[id(proposal)]){next=applyLayout(next,proposal,samples);pages.push(proposal.page);}
   apply(next,pages[0]);setChecked({});
  }}>{t('apply')}</Button>
 </fieldset>;
}
